import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiMapPin,
  FiCpu,
  FiX,
  FiSave,
  FiMap,
  FiHome,
} from "react-icons/fi";
import { useAuth } from "../context/AuthContext";
import { createFarm, updateFarm, getMyFarms, deleteFarm } from "../api/farmApi";
import { toast } from "react-toastify";

const ManageFarms = () => {
  const { clientData } = useAuth();
  const [farms, setFarms] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [editingFarm, setEditingFarm] = useState(null);
  const [mcuList] = useState([]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    const loadFarms = async () => {
      try {
        if (!clientData?.client_id) return;
        const result = await getMyFarms(clientData.client_id);
        const formatted = result.map((farm) => ({
          ...farm,
          mcuCount: farm.mcus?.length || 0,
          createdAt: new Date(farm.createdAt).toLocaleDateString(),
        }));
        setFarms(formatted);
      } catch (err) {
    
        toast.info(err.message);
      }
    };

    loadFarms();
  }, [clientData]);

  const openModal = (farm = null) => {
    setEditingFarm(farm);
    setShowModal(true);
    if (farm) {
      reset(farm);
    } else {
      reset({ name: "", location: "" });
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingFarm(null);
    reset();
  };

  const onSubmit = async (data) => {
    if (editingFarm) {
      try {
        const result = await updateFarm(editingFarm.farm_id, data);
        toast.success(result.name + " Farm updated successfully");
        setFarms(
          farms.map((farm) =>
            farm.farm_id === result.farm_id
              ? { ...result, mcuCount: result.mcus?.length || 0 }
              : farm
          )
        );
      } catch (err) {
        toast.error("Update failed");
      }
    } else {
      try {
        const payload = { ...data, client_id: clientData?.client_id };
        const result = await createFarm(payload);
        toast.success(result.name + " Farm added successfully");
        setFarms([
          ...farms,
          {
            ...result,
            mcuCount: result.mcus?.length || 0,
            createdAt: new Date(result.createdAt).toLocaleDateString(),
          },
        ]);
      } catch (err) {
        toast.error("Creation failed");
      }
    }
    closeModal();
  };

  const deleteMyFarm = async (farmId) => {

    if (window.confirm("Are you sure you want to delete this farm?")) {
      try {
        const result=  await deleteFarm(farmId);
        
        toast.success(result.message);
        setFarms(farms.filter((farm) => farm.farm_id !== farmId));
      } catch (err) {
        toast.error("Delete Farm failed");
      }
    };
  }


  const getFarmMCUs = (farmId) => {
    const farm = farms.find((f) => f.farm_id === farmId);
    return farm?.mcus || [];
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Manage Farms</h1>
          <p className="text-gray-600 mt-1">
            Create and manage your mushroom farms
          </p>
        </div>
        <button
          onClick={() => openModal()}
          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <FiPlus className="w-4 h-4 mr-2" />
          Add New Farm
        </button>
      </div>

      {/* Farms Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {farms.map((farm) => (
          <div
            key={farm.farm_id}
            className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden hover:shadow-md transition-shadow"
          >
            {/* Farm Header */}
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <FiHome className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{farm.name}</h3>
                    <p className="text-sm text-gray-600">{farm.location}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => openModal(farm)}
                    className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                  >
                    <FiEdit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => deleteMyFarm(farm.farm_id)}
                    className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <FiTrash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between text-sm">
                <div className="flex items-center text-gray-600">
                  <FiCpu className="w-4 h-4 mr-1" />
                  <span>{farm.mcuCount} MCUs</span>
                </div>
                <span className="text-gray-500">Created {farm.createdAt}</span>
              </div>
            </div>

            {/* MCU List */}
            <div className="p-6">
              <h4 className="font-medium text-gray-900 mb-3">Connected MCUs</h4>
              {getFarmMCUs(farm.farm_id).length > 0 ? (
                <div className="space-y-2">
                  {getFarmMCUs(farm.farm_id).map((mcu) => (
                    <div
                      key={mcu.id}
                      className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center space-x-2">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        <span className="text-sm font-medium text-gray-900">
                          {mcu.name}
                        </span>
                      </div>
                      <span className="text-xs text-gray-500">{mcu.id}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4">
                  <FiCpu className="w-8 h-8 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">No MCUs connected</p>
                  <a href="/mcu" className="text-blue-600 hover:text-blue-700 text-sm font-medium mt-1">
                    Add MCU
                  </a>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Modal for Add/Edit Farm */}
      {showModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
              onClick={closeModal}
            ></div>

            <div className="inline-block w-full max-w-md p-6 my-8 overflow-hidden text-left align-middle transition-all transform bg-white shadow-xl rounded-2xl">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">
                  {editingFarm ? "Edit Farm" : "Add New Farm"}
                </h3>
                <button
                  onClick={closeModal}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg"
                >
                  <FiX className="w-5 h-5" />
                </button>
              </div>

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                {/* Farm Name */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Farm Name
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FiHome className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      {...register("name", {
                        required: "Farm name is required",
                      })}
                      className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      placeholder="Enter farm name"
                    />
                  </div>
                  {errors.name && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.name.message}
                    </p>
                  )}
                </div>

                {/* Location */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Location
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <FiMapPin className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      {...register("location", {
                        required: "Location is required",
                      })}
                      className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                      placeholder="Enter farm location"
                    />
                  </div>
                  {errors.location && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.location.message}
                    </p>
                  )}
                </div>


                {/* Buttons */}
                <div className="flex items-center justify-end space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <FiSave className="w-4 h-4 mr-2" />
                    {editingFarm ? "Update Farm" : "Create Farm"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageFarms;
